// Lager 1 — canonical display-derivation helper for BookingDetailPanel
// (and any other admin view rendering booking summary info).
//
// Centralizes booking-type label, payment-status badge, and player display
// derivation so per-view conditionals don't drift when status enums evolve
// (per the "Display-state derivation needs ONE canonical helper" lesson —
// applied during R1's `reserved` status rollout where 20 sites had ad-hoc
// `booking.status === "confirmed"` checks that all needed updating).
//
// Pure synchronous functions. Easily unit-testable. No Supabase, no React.

import type { Tables } from "@/integrations/supabase/types";

// ─── Booking type derivation ────────────────────────────────────────────

export type BookingType = "private" | "open_match" | "blocked" | "cancelled" | "other";

/**
 * Classify a booking by its primary kind, used for type-tag display in the
 * info panel.
 *
 * Precedence matches the routing precedence used in the schedule grid:
 *   - status='cancelled' → cancelled
 *   - status='blocked'   → blocked
 *   - is_open_match=true → open_match
 *   - otherwise          → private
 *
 * Note: this is for DISPLAY only. Routing precedence (linked_league_match,
 * linked_open_match) is handled separately in `routeBookingClick`.
 */
export function getBookingType(booking: {
  status?: string | null;
  is_open_match?: boolean | null;
}): BookingType {
  if (booking.status === "cancelled") return "cancelled";
  if (booking.status === "blocked") return "blocked";
  if (booking.is_open_match) return "open_match";
  return "private";
}

// ─── Payment status derivation ──────────────────────────────────────────

export type PaymentStatusKind =
  | "free"          // 0 kr / no payment expected
  | "paid"          // All booking_payments rows are 'paid'
  | "pending"       // Has pending_payment / pending_invoice rows
  | "partial"       // Some paid, some not
  | "refunded"      // Refunded
  | "unknown";      // No payment rows AND nonzero price — uncommon, surfaces as 'Ej registrerad'

export interface PaymentStatusInfo {
  kind: PaymentStatusKind;
  /** Total amount in öre across all payment rows (for display) */
  totalAmountMinor: number;
  /** Total amount marked paid in öre */
  paidAmountMinor: number;
  /** Number of payment rows */
  paymentCount: number;
}

/**
 * Derive a single payment-status kind from a booking + its payment rows.
 * Handles 0-kr bookings (free), fully paid, partial, refunded, and the
 * unknown edge-case where price > 0 but no payment rows exist.
 *
 * `bookings.payment_status` is NOT the canonical source — it only reflects
 * the BOOKER's initial obligation, not the actual ledger. Per Lesson 16
 * (audit-log is canonical truth), aggregate `booking_payments` instead.
 */
export function derivePaymentStatus(
  booking: {
    price_total?: number | null;
    price_total_minor?: number | null;
    payment_status?: string | null;
  },
  payments: Array<{
    amount?: number | null;
    amount_minor?: number | null;
    status?: string | null;
    refunded_at?: string | null;
  }>,
): PaymentStatusInfo {
  // Total expected price (öre) — prefer minor column, fall back to legacy kr × 100.
  const priceMinor =
    typeof booking.price_total_minor === "number" && Number.isFinite(booking.price_total_minor)
      ? booking.price_total_minor
      : Math.round((booking.price_total ?? 0) * 100);

  // 0-kr booking: always "free" regardless of payment row presence.
  if (priceMinor === 0) {
    return { kind: "free", totalAmountMinor: 0, paidAmountMinor: 0, paymentCount: 0 };
  }

  if (payments.length === 0) {
    return {
      kind: "unknown",
      totalAmountMinor: priceMinor,
      paidAmountMinor: 0,
      paymentCount: 0,
    };
  }

  const toMinor = (p: { amount?: number | null; amount_minor?: number | null }) =>
    typeof p.amount_minor === "number" && Number.isFinite(p.amount_minor)
      ? p.amount_minor
      : Math.round((p.amount ?? 0) * 100);

  const totalAmountMinor = payments.reduce((sum, p) => sum + toMinor(p), 0);
  const paidAmountMinor = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + toMinor(p), 0);

  // Any refunded_at on any row → refunded (display-wise; partial refunds
  // also surface here so admin sees the refund indicator).
  if (payments.some((p) => p.refunded_at != null) || payments.some((p) => p.status === "refunded")) {
    return { kind: "refunded", totalAmountMinor, paidAmountMinor, paymentCount: payments.length };
  }

  if (paidAmountMinor === 0) {
    return { kind: "pending", totalAmountMinor, paidAmountMinor, paymentCount: payments.length };
  }
  if (paidAmountMinor >= totalAmountMinor) {
    return { kind: "paid", totalAmountMinor, paidAmountMinor, paymentCount: payments.length };
  }
  return { kind: "partial", totalAmountMinor, paidAmountMinor, paymentCount: payments.length };
}

// ─── Player display derivation ──────────────────────────────────────────

export interface PlayerRowDisplay {
  /** Stable key for React iteration (user_id or fallback) */
  key: string;
  /** Display name; "Okänd" fallback for missing data */
  displayName: string;
  /** Optional category label (e.g. "Medlem") shown as a small badge */
  categoryLabel: string | null;
  /** True when this row is the booker (creator of the booking) */
  isBooker: boolean;
}

/** Unknown-player fallback label — kept stable for both i18n and tests. */
export const UNKNOWN_PLAYER_LABEL = "Okänd";

/**
 * Resolve a single player row (booker or invited) into a stable display
 * shape. Handles missing profile / customer / category data with graceful
 * "Okänd" fallback per the prompt edge-case requirement.
 *
 * Input shape: a `booking_players` row + optional embedded profile +
 * customer with category. Or for the booker: pass user_id + booker's
 * profile/customer separately.
 */
export function resolvePlayerDisplay(args: {
  userId: string | null;
  fullName?: string | null;
  categorySlug?: string | null;
  categoryName?: string | null;
  isBooker: boolean;
}): PlayerRowDisplay {
  const name = (args.fullName ?? "").trim();
  const displayName = name.length > 0 ? name : UNKNOWN_PLAYER_LABEL;
  // Don't display "Standard" as a badge — only show meaningful categories
  // (medlem, ungdom, pensionär, etc).
  const categoryLabel =
    args.categorySlug && args.categorySlug !== "standard" && args.categoryName
      ? args.categoryName.trim()
      : null;
  return {
    key: args.userId ?? `__no_user_${args.fullName ?? "anon"}__`,
    displayName,
    categoryLabel,
    isBooker: args.isBooker,
  };
}

// ─── Open-match seat derivation ─────────────────────────────────────────

export interface OpenMatchSeatInfo {
  /** Total seats (court max_players) */
  maxSeats: number;
  /** Filled seats (count of confirmed/joined players) */
  filledSeats: number;
  /** Empty seats (max - filled, clamped to 0) */
  emptySeats: number;
}

/**
 * For open-match bookings, derive seat occupancy from court max_players
 * and the actual joined-players count. Used to render the "X / Y platser
 * fyllda" line + N empty placeholders.
 *
 * Lesson 18 — when displaying open-match occupancy, the full participant
 * count comes from `booking_players` (the exhaustive reservation source
 * for THIS booking), not just the booker. Pass `joinedPlayersCount` as
 * the result of `SELECT COUNT(*) FROM booking_players WHERE booking_id = X`.
 */
export function deriveOpenMatchSeats(
  maxPlayers: number | null | undefined,
  joinedPlayersCount: number,
): OpenMatchSeatInfo {
  const maxSeats = Math.max(0, maxPlayers ?? 0);
  const filledSeats = Math.max(0, joinedPlayersCount);
  const emptySeats = Math.max(0, maxSeats - filledSeats);
  return { maxSeats, filledSeats, emptySeats };
}

// ─── Re-export booking shape used by panel ──────────────────────────────

export type BookingRow = Tables<"bookings">;
export type BookingPlayerRow = Tables<"booking_players">;
